import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CollaborationsTasksSlice, CollaborationTask, CollaborationTasksSummary } from "../../types/collaborations/CollaborationTaskTypes";
import { generateUniqueKey, getErrorMessage, isFresh } from "../../utils/helpers";
import { apiConfigs } from "../../constants/apiConfigs";
import { ERROR } from "../../constants/errors";
import { fetchCollaborationTasks, fetchCollaborationTasksSummary } from "../../services/api/collaboration";

const initialState: CollaborationsTasksSlice = {
    filters: {
        page: 1
    },
    applied_filter_id: null,
    total: 0,
    filtered_lists: {},
    loading: true,
    error: null,
    gigTypes: [],
    gigSteps: [],
    summary: {
        data: {
            steps: {}
        },
        last_fetched_at: null,
        loading: true,
        error: null
    }
}

export const fetchCollaborationsTasksThunk = createAsyncThunk<{ count: number, items: CollaborationTask[], gig_types: any[], gig_steps: any[], filters: { page: number, [key: string]: any }, filterId: string }, { filters: { page: number, [key: string]: any }, force?: boolean }, { rejectValue: string }>(
    'collaboration/task',
    async ({ filters, force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { collaborationTasks: CollaborationsTasksSlice };
            const filterId = generateUniqueKey(filters, ['page']);
            const STALE_TIME = apiConfigs.FETCH_COLLABORATIONS_TASKS.staleTime;

            if (filters.page === 1 && !force && isFresh(state.collaborationTasks.filtered_lists?.[filterId]?.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const { count, items, gig_types, gig_steps } = await fetchCollaborationTasks({ ...filters, limit: 24 });

            return { count, items, filters, filterId, gig_types, gig_steps }

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration!"));
        }
    }
)

export const fetchCollaborationTasksSummaryThunk = createAsyncThunk<{ summary: CollaborationTasksSummary }, { force?: boolean }, { rejectValue: string }>(
    'collaboration/task/summary',
    async ({ force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { collaborationTasks: CollaborationsTasksSlice };
            const STALE_TIME = apiConfigs.FETCH_COLLABORATION_TASKS_SUMMARY.staleTime;

            if (!force && isFresh(state.collaborationTasks.summary.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const summary = await fetchCollaborationTasksSummary();

            return { summary }

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch collaboration task summary!"));
        }
    }
)


const collaborationTasks = createSlice({
    name: 'collaborationTasks',
    initialState,
    reducers: {
        updateCollaborationTasksFilters: (state, action) => {
            state.filters = action.payload;
        },
        resetCollaborationTasks: (state) => {
            state.applied_filter_id = null;
            state.filtered_lists = {}
        },
        resetTasksSummary: (state) => {
            state.summary.last_fetched_at = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCollaborationsTasksThunk.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchCollaborationsTasksThunk.fulfilled, (state, { payload }) => {
                const { filterId, filters, count, items, gig_steps, gig_types } = payload;
                const existingList = state.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? items : [...existingList, ...items]
                state.gigSteps = gig_steps;
                state.gigTypes = gig_types;
                state.filtered_lists = {
                    ...state.filtered_lists,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count
                    }
                }
                state.applied_filter_id = filterId
                state.loading = false;
                state.error = null;

            })
            .addCase(fetchCollaborationsTasksThunk.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.error = action.payload || 'Failed to fetch tasks'
            })
            .addCase(fetchCollaborationTasksSummaryThunk.pending, (state) => {
                state.summary.loading = true;
                state.summary.error = null;
            })
            .addCase(fetchCollaborationTasksSummaryThunk.fulfilled, (state, { payload }) => {
                state.summary = {
                    data: payload.summary,
                    last_fetched_at: Date.now(),
                    loading: false,
                    error: null
                }
            })
            .addCase(fetchCollaborationTasksSummaryThunk.rejected, (state) => {
                state.summary.loading = false;
            })
    }
})

export const { updateCollaborationTasksFilters, resetCollaborationTasks, resetTasksSummary } = collaborationTasks.actions;
export const collaborationTasksReducer = collaborationTasks.reducer;